import Images from "config/Images";
import React, { useEffect, useState } from "react";
import {
  CloseButton,
  CommentContainer,
  CommentContent,
  CommentContentWrapper,
  CommentDetails,
  CommentFooter,
  CommentImage,
  CommentInput,
  CommentOwner,
  CommentsContainer,
  CommentsFiltering,
  LikeButton,
  PostCommentsContainer,
  PostImage,
  PostImageContainer,
  PostWindowContainer,
  ReplyButton,
  ReplyContainer,
  RightFooter,
  ToggleRepliesButton,
  WriteCommentContainer,
} from "./PostCard.styles";

import {
  Content,
  Likes,
  NameAndDate,
  OrganizationName,
  OrgInformation,
  ProfilePicture,
  TimePosted,
} from "./PostCard.styles";

import { formatTime } from "./PostFiltering";

import {
  createComment,
  getComments,
  getPostsById,
  getReplies,
  setCommentLiked,
} from "apis/PostServiceApi";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "store/types";
import { User } from "types";

const PostComments = () => {
  const { postId } = useParams();

  const auth = useSelector((state: RootState) => state.auth);
  const [post, setPost] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState<string>("");
  const [showReplies, setShowReplies] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [replyTo, setReplyTo] = useState<number | null>(null);

  const [newReplies, setNewReplies] = useState<Record<number, string>>({});
  const navigate = useNavigate();

  const userData = useSelector((state: any) => {
    return {
      profilePicture: state.user.socialAvatarUrl,
    };
  });

  const fetchComments = async () => {
    try {
      if (postId) {
        const response = await getComments({
          comment_type: "POST",
          object_id: Number(postId),
          page: 1,
          limit: 10,
        });

        const formattedComments = response.data.map((comment: any) => ({
          ...comment,
          isCurrentLiked:
            Array.isArray(comment.likes) &&
            comment.likes.some((like: any) => like.id === auth.userId),
        }));

        setComments(formattedComments);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const fetchPosts = async () => {
    try {
      if (postId) {
        const response = await getPostsById(Number(postId));
        response.postId = response.id;
        response.organizationName =
          response.user?.name || "Unknown Organization";
        response.description = response.post_caption || "";
        response.isCurrentLiked = response.likes.some(
          (like: any) => like.pivot?.user_id === auth.userId
        );
        response.likesCount = response.likes_count ?? 0;
        response.comments = response.commentsCount || 0;

        response.profilePictureURL = response.user?.socialAvatarUrl || "";
        response.contentPicture = response.images?.[0]?.image_url || "";
        response.time = response.created_at || "";
        response.likesProfiles = response.likes.map((like: any) => ({
          profilePictureURL: like.socialAvatarUrl || "",
          name: like.name || "Unknown",
        }));
        response.shares = 0;
        setPost(response);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  useEffect(() => {
    fetchPosts();
    fetchComments();
  }, [postId]);

  const fetchReplies = async (commentId: number) => {
    try {
      const response = await getReplies({
        parent_id: commentId,
        page: 1,
        limit: 10,
      });

      const formattedReplies = response.data.map((reply: any) => ({
        ...reply,
        isCurrentLiked:
          Array.isArray(reply.likes) &&
          reply.likes.some((like: any) => like.id === auth.userId),
      }));

      setComments((prevComments) =>
        prevComments.map((comment) =>
          comment.comment_id === commentId
            ? { ...comment, replies: formattedReplies }
            : comment
        )
      );
    } catch (error) {
      console.error("Error fetching replies:", error);
    }
  };

  const addComment = (postId: number) => {
    if (!newComment.trim()) {
      console.error("El comentario no puede estar vacío.");
      return;
    }

    createComment(postId, newComment)
      .then((response) => {
        setNewComment("");
        fetchComments();
        setReplyTo(null);
      })
      .catch((error) => {
        console.error("Error al crear el comentario:", error);
      });
  };

  const addReply = (postId: number, parentId: number) => {
    const replyContent = newReplies[parentId]?.trim();

    if (!replyContent) {
      console.error("La respuesta no puede estar vacía.");
      return;
    }

    createComment(postId, replyContent, parentId)
      .then((response) => {
        setNewReplies((prev) => ({ ...prev, [parentId]: "" }));
        setReplyTo(null);
        fetchReplies(parentId);
      })
      .catch((error) => {
        console.error("Error al crear la respuesta:", error);
      });
  };

  const toggleLiked = (
    commentId: number,
    isReply: boolean = false,
    parentId?: number
  ) => {
    const updatedComments = comments.map((comment) => {
      if (isReply) {
        if (comment.comment_id === parentId) {
          return {
            ...comment,
            replies: comment.replies.map((reply: any) =>
              reply.comment_id === commentId
                ? {
                  ...reply,
                  isCurrentLiked: !reply.isCurrentLiked,
                  like_count: reply.isCurrentLiked
                    ? reply.like_count - 1
                    : reply.like_count + 1,
                }
                : reply
            ),
          };
        }
      } else {
        if (comment.comment_id === commentId) {
          return {
            ...comment,
            isCurrentLiked: !comment.isCurrentLiked,
            like_count: comment.isCurrentLiked
              ? comment.like_count - 1
              : comment.like_count + 1,
          };
        }
      }
      return comment;
    });

    setComments(updatedComments);

    setCommentLiked(commentId, auth.userId!)
      .then(() => {
        console.log(
          `${isReply ? "Reply" : "Comment"
          } ${commentId} liked/unliked successfully`
        );
      })
      .catch((error) => {
        console.error("Error toggling like:", error);
        setComments((prevComments) =>
          prevComments.map((prevComment) => {
            if (isReply) {
              if (prevComment.comment_id === parentId) {
                return {
                  ...prevComment,
                  replies: prevComment.replies.map((reply: any) =>
                    reply.comment_id === commentId
                      ? {
                        ...reply,
                        isCurrentLiked: !updatedComments
                          .find((c) => c.comment_id === parentId)
                          ?.replies.find(
                            (r: { comment_id: number }) =>
                              r.comment_id === commentId
                          )?.isCurrentLiked,
                      }
                      : reply
                  ),
                };
              }
            } else if (prevComment.comment_id === commentId) {
              return {
                ...prevComment,
                isCurrentLiked: !prevComment.isCurrentLiked,
              };
            }
            return prevComment;
          })
        );
      });
  };

  const toggleReplies = (commentId: number) => {
    if (
      !comments.find((comment) => comment.comment_id === commentId)?.replies
    ) {
      fetchReplies(commentId);
    }

    setShowReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === "Enter") {
      e.preventDefault();
      action();
    }
  };

  const handleCloseComment = () => {
    navigate(-1);
  };

  const handleProfileClick = (userId: User["id"]) => {
    navigate(`/profile/${userId}`);
  };

  return (
    <>
      {post && (
        <PostWindowContainer>
          {/* Left: Image */}
          <PostImageContainer>
            <CloseButton onClick={handleCloseComment}>
              <img src={Images.closeIcon} alt="Close" />
            </CloseButton>

            <PostImage src={post.contentPicture} alt="Post" />
          </PostImageContainer>

          {/* Right: PostInfo */}
          <PostCommentsContainer>
            <OrgInformation>
              <ProfilePicture
                src={post.profilePictureURL}
                onClick={() => handleProfileClick(post.postOwnerId)}
                style={{ cursor: "pointer" }}
              />
              <NameAndDate>
                <OrganizationName
                  onClick={() => handleProfileClick(post.postOwnerId)}
                  style={{ cursor: "pointer" }}
                >
                  {post.organizationName}
                </OrganizationName>
                <TimePosted>{formatTime(post.time)}</TimePosted>
              </NameAndDate>
              <Content>{post.description}</Content>
            </OrgInformation>
            {/* <BottomSection>
              <PostDetails>
                <Likes>
                  <div>
                    {post.likesProfiles?.slice(0, 3).map((user, index) => (
                      <img
                        key={index}
                        src={user.profilePictureURL}
                        alt={`User ${index + 1}`}
                      />
                    ))}
                  </div>
                  <span>
                    {post.likesCount} Like{post.likesCount === 1 ? "" : "s"}
                  </span>
                </Likes>
                <CommentsAndShares>
                  {post.comments} Comments • {post.shares} Shares
                </CommentsAndShares>
              </PostDetails>
              <CommentsPostActions>
                <PostActions>
                  <LikeAction
                    prevPost={post}
                    postId={post.postId}
                    postOwnerId={post.postOwnerId}
                    isLiked={post.isCurrentLiked}
                    likesCount={post.likesCount}
                    setPosts={setPosts}
                    setSelectedPost={setSelectedPost}
                  />

                  <CommentAction
                    post={post}
                    setShowModal={setShowModal}
                    setSelectedPost={setSelectedPost}
                  />

                  <ShareAction />

                  <SaveAction />
                </PostActions>
              </CommentsPostActions>
            </BottomSection> */}

            <CommentsFiltering>Most Recent</CommentsFiltering>
            <CommentsContainer>
              {comments.map((comment, index) => (
                <React.Fragment key={index}>
                  {/* Comment */}
                  <CommentContainer>
                    <CommentImage
                      src={comment.user_avatar}
                      alt="Avatar"
                      onClick={() => handleProfileClick(comment.user_id)}
                      style={{ cursor: "pointer" }}
                    />
                    <CommentContentWrapper>
                      <CommentDetails>
                        <CommentOwner
                          onClick={() => handleProfileClick(comment.user_id)}
                          style={{ cursor: "pointer" }}
                        >
                          {comment.user_name}
                        </CommentOwner>
                        <CommentContent>{comment.comment_body}</CommentContent>
                      </CommentDetails>
                      {/* Comment Footer */}
                      <CommentFooter>
                        <RightFooter>
                          <span>{formatTime(comment.created_at)}</span>
                          <LikeButton
                            onClick={() => toggleLiked(comment.comment_id)}
                          >
                            {comment.isCurrentLiked ? "Unlike" : "Like"}
                          </LikeButton>
                          <ReplyButton
                            onClick={() => toggleReplies(comment.comment_id)}
                          >
                            Reply
                          </ReplyButton>
                        </RightFooter>
                        <Likes>
                          {comment.like_count} Like
                          {comment.like_count === 1 ? "" : "s"}
                        </Likes>
                      </CommentFooter>

                      {/*View replies */}
                      <ToggleRepliesButton
                        onClick={() => toggleReplies(comment.comment_id)}
                      >
                        {showReplies[comment.comment_id]
                          ? "Hide replies"
                          : `View replies`}
                      </ToggleRepliesButton>

                      {/* Replies */}
                      {showReplies[comment.comment_id] &&
                        Array.isArray(comment.replies) &&
                        comment.replies.map((reply: any) => (
                          <ReplyContainer key={`reply-${reply.comment_id}`}>
                            <CommentImage
                              src={reply.user_avatar}
                              alt="Avatar"
                              onClick={() => handleProfileClick(reply.user_id)}
                              style={{ cursor: "pointer" }}
                            />
                            <CommentContentWrapper>
                              <CommentDetails>
                                <CommentOwner
                                  onClick={() =>
                                    handleProfileClick(reply.user_id)
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  {reply.user_name}
                                </CommentOwner>
                                <CommentContent>
                                  {reply.comment_body}
                                </CommentContent>
                              </CommentDetails>
                              <CommentFooter>
                                <RightFooter>
                                  <span>{formatTime(reply.created_at)}</span>
                                  <LikeButton
                                    onClick={() =>
                                      toggleLiked(
                                        reply.comment_id,
                                        true,
                                        comment.comment_id
                                      )
                                    }
                                  >
                                    {reply.isCurrentLiked ? "Unlike" : "Like"}
                                  </LikeButton>
                                </RightFooter>
                                <Likes>
                                  {reply.like_count} Like
                                  {reply.like_count === 1 ? "" : "s"}
                                </Likes>
                              </CommentFooter>
                            </CommentContentWrapper>
                          </ReplyContainer>
                        ))}

                      {/*"Reply to" */}
                      {(replyTo === comment.comment_id ||
                        showReplies[comment.comment_id]) && (
                          <WriteCommentContainer>
                            <CommentImage
                              src={userData.profilePicture}
                              alt="Avatar"
                            />
                            <CommentInput
                              placeholder={`Reply to ${comment.user_name}`}
                              value={newReplies[comment.comment_id] || ""}
                              onChange={(e) =>
                                setNewReplies((prev) => ({
                                  ...prev,
                                  [comment.comment_id]: e.target.value,
                                }))
                              }
                              onKeyDown={(e) =>
                                handleKeyDown(e, () =>
                                  addReply(Number(postId), comment.comment_id)
                                )
                              }
                            />
                          </WriteCommentContainer>
                        )}
                    </CommentContentWrapper>
                  </CommentContainer>
                </React.Fragment>
              ))}

              {/*"Write a comment"*/}
              <WriteCommentContainer>
                <CommentImage src={userData.profilePicture} alt="Avatar" />
                <CommentInput
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) =>
                    handleKeyDown(e, () => addComment(Number(postId)))
                  }
                />
              </WriteCommentContainer>
            </CommentsContainer>
          </PostCommentsContainer>
        </PostWindowContainer>
      )}
    </>
  );
};
export default PostComments;
